import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getProductTypes, headerDetailData, tableDetailData } from '../../../api/knowledgeProductApi';
import { TimeRange } from '../../../models/TimeRangeKnowledgeProductsEnum';
import KnowledgeProductTable from '../KnowledgeProductTable/KnowledgeProductTable';
import SummaryHeaderSingleValue from '../SummaryHeaderSingleValue/SummaryHeaderSingleValue';
import styles from './KnowledgeProductDetail.module.scss';
import {
  getCountries,
  getMaxDate,
  getMinDate,
  multipleFilter,
  searcherAndFilter,
  setObjectParams,
  setRangeDateByDays,
} from './Util';
import { isEqual } from 'lodash';
import KnowledgeProductFilterBubbles from '../KnowledgeProductFilterBubbles/KnowledgeProductFilterBubbles';
import { AppMenuTabs } from '../../MenuTabs/userTabsInterface';
import { RegionsAcronymEnum } from '../../../models/RegionsAcronymEnum';
import { KnowledgeProductsYearFilterContext } from '../../../context/KnowledgeProductsYearFilterContext';

const setSelectedLink = (seter, stateValue) => {
  return seter({
    ...stateValue,
    selected: !stateValue.selected,
  });
};

const setUnselectedLink = (seter, stateValue) => {
  return seter({
    ...stateValue,
    selected: stateValue.selected === true && !stateValue.selected,
  });
};

const customStyle = { minWidth: 120 };
const customValueStyle = { fontSize: 36 };
const customSubtitleStyle = { fontSize: 16, fontWeight: 600, color: '#264377', paddingTop: 10 };
const customLinkStyle = { fontSize: 16, fontWeight: 300, textDecoration: 'underline', color: '#264377' };

const getYearFromAvailableDate = (value): number | null => {
  const date = new Date(value);
  const year = date.getFullYear();
  return Number.isNaN(year) ? null : year;
};

const getAvailableYearsFromData = (data): number[] => {
  const years: number[] = data
    .map(item => getYearFromAvailableDate(item.availableDate))
    .filter((year): year is number => year !== null);
  const uniqueYears = Array.from(new Set<number>(years));
  return uniqueYears.sort((a: number, b: number) => b - a);
};

const filterKnowledgeProductsByYear = (data, selectedYear: number) => {
  if (!selectedYear) return data;
  return data.filter(item => getYearFromAvailableDate(item.availableDate) === selectedYear);
};

const mapProductTypesToOptions = response => {
  const asArray = Array.isArray(response) ? response : response?.collection || [];
  const mapped = asArray
    .map((item, index) => {
      if (typeof item === 'string') {
        return { id: index + 1, label: item };
      }
      const id = Number(item?.id ?? item?.value ?? index + 1);
      const label = (item?.name ?? item?.label ?? item?.description ?? item?.value ?? '').toString().trim();
      return { id, label };
    })
    .filter(item => item.label.length > 0);

  const deduped = mapped.filter((item, index, array) => index === array.findIndex(other => other.label === item.label));
  return deduped.sort((a, b) => a.label.localeCompare(b.label));
};

const filterKnowledgeProductsByProductType = (data, productTypeOptions, selectedProductTypeId: number) => {
  if (!selectedProductTypeId) return data;
  const selectedProductType = productTypeOptions.find(option => option.id === selectedProductTypeId);
  if (!selectedProductType) return data;
  const selectedLabel = selectedProductType.label.toLowerCase().trim();
  return data.filter(item => (item?.productType || '').toString().toLowerCase().trim() === selectedLabel);
};

const buildProductTypeForSearchContext = (selectedProductTypeId: number, availableProductTypes) => {
  if (!selectedProductTypeId) {
    return [{ value: 'all', id: 0, label: 'all' }];
  }

  const selectedProductType = availableProductTypes.find(option => option.id === selectedProductTypeId);
  if (!selectedProductType) {
    return [{ value: 'all', id: 0, label: 'all' }];
  }

  return [{ value: selectedProductType.label, id: selectedProductType.id, label: selectedProductType.label }];
};

export const searchObject = {
  from: new Date().toISOString() || null,
  to: new Date().toISOString() || null,
  componenteHandleDateRanges: true,
  rangeDate: 30,
  productType: [{ value: 'all', id: 0, label: 'all' }],
  filteredCountries: [{ value: 'all', id: 0, label: 'all' }],
  departmentDivision: [{ value: 'all', id: 0, label: 'all' }],
  countries: [{ value: 'all', id: 0, label: 'all' }],
  disabledCountriesSelect: false,
  modalFilterUsed: false,
  firstLoad: true,
  minDate: new Date().toISOString() || null,
  maxDate: new Date().toISOString() || null,
  searchButtonFired: false,
};

export const SearchObjectContext = createContext([{}, () => { }]);

const defineDefaultSelect = timeRange => {
  const rangeStored = localStorage.getItem('knowledgeIndicatorRange');
  if (!rangeStored) {
    return timeRange === TimeRange.Month_1;
  }

  return rangeStored === timeRange.toString();
};

const defineDefaultRangeDate = () => {
  const rangeStored = localStorage.getItem('knowledgeIndicatorRange');
  if (rangeStored) {
    return parseInt(rangeStored);
  } else {
    return TimeRange.Month_1;
  }
};

const getKnowledgeRangeLabel = (rangeStored: string | null) => {
  switch (rangeStored) {
    case '180':
      return 'published_in_the_last_6_months';
    case '365':
      return 'published_in_the_last_year';
    case '0':
      return 'all_published';
    default:
      return 'published_in_the_last_30_days';
  }
};

type Props = {
  headerTitle: any;
};
const KnowledgeProductDetail = ({ headerTitle }: Props) => {
  const { t } = useTranslation();
  const { layout, header, table } = styles
  const [last30DaysState, setLast30DaysState] = useState({
    value: 0,
    selected: defineDefaultSelect(TimeRange.Month_1),
  });
  const [last6MonthsState, setLast6MonthsState] = useState({
    value: 0,
    selected: defineDefaultSelect(TimeRange.Month_6),
  });
  const [last12MonthsState, setLast12MonthsState] = useState({
    value: 0,
    selected: defineDefaultSelect(TimeRange.Month_12),
  });
  const [everyoneState, setEveryoneState] = useState({ value: 0, selected: defineDefaultSelect(TimeRange.Everyone) });
  const [tableData, setTableData] = useState([]);
  const [renderData, setRenderData] = useState([]);
  const [filteredModalUsedData, setFilteredModalUsedData] = useState([]);
  const [rangeDate, setRangeDate] = useState(setRangeDateByDays(defineDefaultRangeDate()));
  const [searchContext, setSearchContext] = useState(searchObject);
  const filterIndicatorsCommon = useSelector<any, any>(state => state.filterIndicators.common);
  const { regionId: regionsFilterIndicator } = filterIndicatorsCommon;
  const { portfolioSelectedTab } = useSelector<any, AppMenuTabs>(state => state.menuTabs);
  const { regions } = useSelector<any, any>(state => state);
  const {
    selectedYear,
    setSelectedYear,
    setAvailableYears,
    selectedProductTypeId,
    setSelectedProductTypeId,
    availableProductTypes,
    setAvailableProductTypes,
  } = useContext(KnowledgeProductsYearFilterContext);

  const [filterBtnData, setFilterBtnData] = useState<boolean>(false);

  const applySelectedKnowledgeFilters = dataToFilter => {
    const countryFiltered = applyGlobalCountryFilter(dataToFilter);
    const yearFiltered = filterKnowledgeProductsByYear(countryFiltered, selectedYear);
    return filterKnowledgeProductsByProductType(yearFiltered, availableProductTypes, selectedProductTypeId);
  };

  const loadKnowledgeProductTypes = async () => {
    try {
      const productTypesResponse = await getProductTypes();
      const options = mapProductTypesToOptions(productTypesResponse);
      setAvailableProductTypes(options);
      if (selectedProductTypeId && !options.find(option => option.id === selectedProductTypeId)) {
        setSelectedProductTypeId(0);
      }
    } catch (error) {
      setAvailableProductTypes([]);
    }
  };

  const applyGlobalCountryFilter = dataToFilter => {
    const selectedCountries = filterIndicatorsCommon?.countryId;
    if (!selectedCountries || selectedCountries.length === 0) {
      return dataToFilter;
    }

    const selectedNames = new Set(
      selectedCountries
        .map(country => (country.label || country.value || '').toString().toLowerCase().trim())
        .filter(name => name.length > 0),
    );
    const selectedIds = new Set(
      selectedCountries.map(country => country.id).filter(id => id !== null && id !== undefined),
    );

    return dataToFilter.filter(product => {
      const countryName = product?.countryName ? product.countryName.toString().toLowerCase().trim() : '';
      const altCountryName = product?.country ? product.country.toString().toLowerCase().trim() : '';
      const responsibleCountryName = product?.responsibleCountryName
        ? product.responsibleCountryName.toString().toLowerCase().trim()
        : '';
      const countryId = product?.countryId ?? product?.responsibleCountryId;

      const matchesByName =
        (countryName && selectedNames.has(countryName)) ||
        (altCountryName && selectedNames.has(altCountryName)) ||
        (responsibleCountryName && selectedNames.has(responsibleCountryName));
      const matchesById = countryId !== null && countryId !== undefined && selectedIds.has(countryId);

      return matchesByName || matchesById;
    });
  };

  const getGlobalCountryIds = () =>
    filterIndicatorsCommon?.countryId
      ? filterIndicatorsCommon.countryId
        .map(country => country.id)
        .filter(id => id !== null && id !== undefined)
      : [];

  const handleGetKnowledgeHeaderDetail = async () => {
    const objectParams = setObjectParams(searchContext);
    const data: any = await Promise.all([headerDetailData(objectParams), tableDetailData(rangeDate)]);
    const years = getAvailableYearsFromData(data[1]);
    setLast30DaysState({ ...last30DaysState, value: data[0].totalQuantityLast30Days });
    setLast6MonthsState({ ...last6MonthsState, value: data[0].totalQuantityLast180Days });
    setLast12MonthsState({ ...last12MonthsState, value: data[0].totalQuantityLast365Days });
    setEveryoneState({ ...everyoneState, value: data[0].totalQuantity });
    setAvailableYears(years);
    if (selectedYear && !years.includes(selectedYear)) {
      setSelectedYear(0);
    }
    setTableData(data[1]);
    const multipleFilterData = multipleFilter(data[1], searchContext);
    setRenderData(applySelectedKnowledgeFilters(multipleFilterData));
  };

  useEffect(() => {
    setIndicatorTitle();
    loadKnowledgeProductTypes();
    setSearchContext({
      ...searchContext,
      from: setRangeDateByDays(TimeRange.Month_1).from,
      to: setRangeDateByDays(TimeRange.Month_1).to,
      rangeDate: TimeRange.Month_1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIndicatorTitle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeDate]);

  const updateSearchContextOnrenderDataChange = (min, max) => {
    setSearchContext({
      ...searchContext,
      from: min,
      to: max,
      minDate: min,
      maxDate: max,
      firstLoad: false,
      rangeDate: defineDefaultRangeDate(),
    });
  };

  useEffect(() => {
    if (
      (searchContext.rangeDate === TimeRange.Everyone && searchContext.componenteHandleDateRanges) ||
      searchContext.firstLoad
    ) {
      const minDate = getMinDate(renderData);
      const maxDate = getMaxDate(renderData);
      updateSearchContextOnrenderDataChange(minDate, maxDate);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderData]);

  useEffect(() => {
    handleGetKnowledgeHeaderDetail();
    if (searchContext.firstLoad) {
      setSearchContext({
        ...searchContext,
        from: setRangeDateByDays(searchContext.rangeDate).from,
        to: setRangeDateByDays(searchContext.rangeDate).to,
        rangeDate: searchContext.rangeDate,
        firstLoad: false,
      });
      setCountriesToFilter();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeDate]);

  useEffect(() => {
    handleGetKnowledgeHeaderDetail();
    setCountriesToFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicatorsCommon]);

  useEffect(() => {
    setSearchContext(prev => ({
      ...prev,
      productType: buildProductTypeForSearchContext(selectedProductTypeId, availableProductTypes),
      searchButtonFired: !prev.searchButtonFired,
    }));
  }, [selectedProductTypeId, availableProductTypes]);

  useEffect(() => {
    handleGetKnowledgeHeaderDetail();
  }, [searchContext.productType]);

  useEffect(() => {
    const baseData = searchContext.modalFilterUsed ? filteredModalUsedData : multipleFilter(tableData, searchContext);
    setRenderData(applySelectedKnowledgeFilters(baseData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedProductTypeId]);

  const handleRangeDate = rangeSelected => {
    setFilterBtnData(prev => !prev);
    setRangeDate(setRangeDateByDays(rangeSelected));
    const minDate = getMinDate(renderData);
    const maxDate = getMaxDate(renderData);
    setSearchContext({
      ...searchContext,
      from: setRangeDateByDays(rangeSelected).from !== null ? setRangeDateByDays(rangeSelected).from : minDate,
      to: setRangeDateByDays(rangeSelected).to !== null ? setRangeDateByDays(rangeSelected).to : maxDate,
      rangeDate: rangeSelected,
      searchButtonFired: !searchContext.searchButtonFired,
      componenteHandleDateRanges: true,
    });
  };

  const updateFilterModalUsed = tableDataIsOriginal => {
    setSearchContext({
      ...searchContext,
      modalFilterUsed: !tableDataIsOriginal,
      searchButtonFired: !searchContext.searchButtonFired,
    });
  };

  const handleAdvancedSearch = async () => {
    const objectParams = setObjectParams(searchContext);
    const updateHeaderDetailValues: any = await headerDetailData(objectParams);
    const multipleFilterData = multipleFilter(tableData, searchContext);
    const tableDataIsOriginal = isEqual(tableData, multipleFilterData);
    updateFilterModalUsed(tableDataIsOriginal);
    setFilteredModalUsedData(multipleFilterData);
    setRenderData(applySelectedKnowledgeFilters(multipleFilterData));
    setLast30DaysState({ ...last30DaysState, value: updateHeaderDetailValues.totalQuantityLast30Days });
    setLast6MonthsState({ ...last6MonthsState, value: updateHeaderDetailValues.totalQuantityLast180Days });
    setLast12MonthsState({ ...last12MonthsState, value: updateHeaderDetailValues.totalQuantityLast365Days });
    setEveryoneState({ ...everyoneState, value: updateHeaderDetailValues.totalQuantity });
  };

  const handleSearchTerm = e => {
    const searchTerm = e.target.value.toLowerCase();
    const baseData = searchContext.modalFilterUsed ? filteredModalUsedData : tableData;
    setRenderData(searcherAndFilter(searchTerm, applySelectedKnowledgeFilters(baseData)));
  };

  const setCountriesToFilter = () => {
    if (
      portfolioSelectedTab.internalId === '0_global_bid' ||
      (regionsFilterIndicator.length &&
        (portfolioSelectedTab.title === RegionsAcronymEnum.CAN ||
          portfolioSelectedTab.title === RegionsAcronymEnum.CCB ||
          portfolioSelectedTab.title === RegionsAcronymEnum.CID ||
          portfolioSelectedTab.title === RegionsAcronymEnum.CSC ||
          portfolioSelectedTab.title === RegionsAcronymEnum.REG))
    ) {
      const haveRegionsInfilter = regionsFilterIndicator.length ? regionsFilterIndicator.map(r => r.label) : [];
      setSearchContext({
        ...searchContext,
        countries: getCountries(regions, haveRegionsInfilter),
        disabledCountriesSelect: false,
      });
    } else {
      setSearchContext({
        ...searchContext,
        countries: getCountries(regions),
        disabledCountriesSelect: false,
      });
    }
  };

  const setIndicatorTitle = () => {
    const rangeStored = localStorage.getItem('knowledgeIndicatorRange');
    const knowledgeRangeLabel = getKnowledgeRangeLabel(rangeStored);
    headerTitle(true, `${t('knowledge_products')} ${t(knowledgeRangeLabel)}`);
  };

  const linkStateOptions = {
    setLast30DaysState,
    last30DaysState,
    setLast6MonthsState,
    last6MonthsState,
    setLast12MonthsState,
    last12MonthsState,
    setEveryoneState,
    everyoneState,
  };

  return (
    <>
      <div className={layout}>
        <KnowledgeProductFilterBubbles searchContext={searchContext} />
        <div className={header}>
          {renderSummaryHeaderSingleValues(linkStateOptions, handleRangeDate, t)}
        </div>
        <div className={table}>
          <KnowledgeProductTable tableData={renderData} filterBtnData={filterBtnData} />
        </div>
      </div>
    </>
  );
};

export default KnowledgeProductDetail;

const renderSummaryHeaderSingleValues = (linkStateOptions, handleRangeDate, t) => {
  const { last30DaysState, last6MonthsState, last12MonthsState, everyoneState } = linkStateOptions;
  return (
    <>
      {' '}
      <SummaryHeaderSingleValue
        value={last30DaysState.value}
        title={t('last_30_days')}
        customStyle={customStyle}
        customValueStyle={customValueStyle}
        customSubtilteStyle={customSubtitleStyle}
        customLinkStyle={customLinkStyle}
        isLink={true}
        isSelected={last30DaysState.selected}
        callbackLink={() => {
          handleSelectedLink(TimeRange.Month_1, handleRangeDate, linkStateOptions);
        }}
      />
      <SummaryHeaderSingleValue
        value={last6MonthsState.value}
        title={t('last_6_months')}
        customStyle={customStyle}
        customValueStyle={customValueStyle}
        customSubtilteStyle={customSubtitleStyle}
        customLinkStyle={customLinkStyle}
        isLink={true}
        isSelected={last6MonthsState.selected}
        callbackLink={() => {
          handleSelectedLink(TimeRange.Month_6, handleRangeDate, linkStateOptions);
        }}
      />
      <SummaryHeaderSingleValue
        value={last12MonthsState.value}
        title={t('last_12_months')}
        customStyle={customStyle}
        customValueStyle={customValueStyle}
        customSubtilteStyle={customSubtitleStyle}
        customLinkStyle={customLinkStyle}
        isSelected={last12MonthsState.selected}
        isLink={true}
        callbackLink={() => {
          handleSelectedLink(TimeRange.Month_12, handleRangeDate, linkStateOptions);
        }}
      />
      <SummaryHeaderSingleValue
        value={everyoneState.value}
        title={t('everyone')}
        customStyle={customStyle}
        customValueStyle={customValueStyle}
        customSubtilteStyle={customSubtitleStyle}
        customLinkStyle={customLinkStyle}
        isLink={true}
        isSelected={everyoneState.selected}
        callbackLink={() => {
          handleSelectedLink(TimeRange.Everyone, handleRangeDate, linkStateOptions);
        }}
      />
    </>
  );
};

const handleSelectedLink = (
  rangeSelected = TimeRange.Month_1,
  handleRangeDate,
  {
    setLast30DaysState,
    last30DaysState,
    setLast6MonthsState,
    last6MonthsState,
    setLast12MonthsState,
    last12MonthsState,
    setEveryoneState,
    everyoneState,
  },
) => {
  handleRangeDate(rangeSelected);
  localStorage.setItem('knowledgeIndicatorRange', rangeSelected.toString());
  switch (rangeSelected) {
    case TimeRange.Month_1:
      setSelectedLink(setLast30DaysState, last30DaysState);
      setUnselectedLink(setLast6MonthsState, last6MonthsState);
      setUnselectedLink(setLast12MonthsState, last12MonthsState);
      setUnselectedLink(setEveryoneState, everyoneState);
      break;
    case TimeRange.Month_6:
      setUnselectedLink(setLast30DaysState, last30DaysState);
      setSelectedLink(setLast6MonthsState, last6MonthsState);
      setUnselectedLink(setLast12MonthsState, last12MonthsState);
      setUnselectedLink(setEveryoneState, everyoneState);
      break;
    case TimeRange.Month_12:
      setUnselectedLink(setLast30DaysState, last30DaysState);
      setUnselectedLink(setLast6MonthsState, last6MonthsState);
      setSelectedLink(setLast12MonthsState, last12MonthsState);
      setUnselectedLink(setEveryoneState, everyoneState);
      break;
    default:
      setUnselectedLink(setLast30DaysState, last30DaysState);
      setUnselectedLink(setLast6MonthsState, last6MonthsState);
      setUnselectedLink(setLast12MonthsState, last12MonthsState);
      setSelectedLink(setEveryoneState, everyoneState);
      break;
  }
};
